import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import TriCTA from "../components/TriCTA";

const PHASED_DEPLOYMENT = [
  { phase: "Phase 1: Land & Licensing", year: "Q4 2026", capital: "$3M", body: "SEZA license · land title · environmental approvals · master plan · early Italian partner contracting." },
  { phase: "Phase 2: Tannery", year: "Q2 2027", capital: "$18M", body: "Tannery construction · machinery procurement · water treatment · first hides commissioning." },
  { phase: "Phase 3: Manufacturing", year: "Q4 2027", capital: "$15M", body: "Footwear, handbag and accessories lines · Italian master-craftsmen training programme." },
  { phase: "Phase 4: Export Scale", year: "Q2 2028", capital: "$9M", body: "Working capital ramp · distribution agreements · brand & marketing · Phase 2 expansion design." },
];

const REVENUE_CHANNELS = [
  { name: "Tannery output sales", year_3: "$14M", year_5: "$28M" },
  { name: "Finished goods (footwear)", year_3: "$11M", year_5: "$32M" },
  { name: "Finished goods (handbags & accessories)", year_3: "$9M", year_5: "$26M" },
  { name: "Lease & manufacturing income", year_3: "$3M", year_5: "$9M" },
  { name: "Training & licensing income", year_3: "$1M", year_5: "$3M" },
  { name: "Export distribution margin", year_3: "$2M", year_5: "$7M" },
];

const RETURN_LOGIC = [
  { code: "01", title: "Senior equity tranche", body: "Priority distributions from operating cash flow and tannery sales." },
  { code: "02", title: "Self-liquidating debt structure", body: "Long-tenor structured debt serviced from contracted offtake revenue." },
  { code: "03", title: "Strategic equity upside", body: "Common equity participation in finished-goods margin and brand value." },
  { code: "04", title: "Phase 2 expansion option", body: "Pre-emptive rights into the Phase 2 expansion SPV at favourable terms." },
];

export default function FinancialModel() {
  const totalY3 = "$40M";
  const totalY5 = "$105M";

  return (
    <div data-testid="page-financial-model" className="bg-[#F9F9F6]">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-9">
            <p className="eyebrow">Financial Model</p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight mt-6">
              The investor model. <span className="italic text-[#5C2018]">Cleanly built.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-[#1A1A1A] max-w-2xl leading-relaxed">
              Capital flow, phased deployment, six revenue channels, and a self-liquidating debt structure. Indicative figures presented for institutional review. Full model available under the IM.
            </p>
          </div>
        </div>
      </section>

      {/* Headline strip */}
      <section className="border-y border-[#1A1A1A] bg-[#F9F9F6]" data-testid="model-headline-strip">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
          <Stat label="Capital Required" value="$45M" sub="LRG Leather Botswana SPV" />
          <Stat label="Year 3 Revenue" value={totalY3} sub="Indicative" />
          <Stat label="Year 5 Revenue" value={totalY5} sub="Indicative" />
          <Stat label="Target IRR" value="22-28%" sub="Strategic equity tranche" />
        </div>
      </section>

      {/* Phased deployment */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24" data-testid="model-phased">
        <div className="grid grid-cols-12 gap-8 mb-10">
          <div className="col-span-12 md:col-span-5">
            <p className="eyebrow">01. Phased Deployment</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-4">Capital, drawn in stages.</h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 self-end">
            <p className="text-sm text-[#1A1A1A] leading-relaxed max-w-xl">
              Capital is drawn against milestones. Never sunk in a single tranche. Each phase de-risks the next.
            </p>
          </div>
        </div>
        <div className="border-t border-[#1A1A1A]">
          {PHASED_DEPLOYMENT.map((p, i) => (
            <div key={p.phase} data-testid={`phase-${i}`} className="grid grid-cols-12 gap-6 py-8 border-b border-[#E5E5E0] hover:bg-white transition-colors">
              <div className="col-span-12 md:col-span-1 font-serif text-2xl text-[#5C2018]">0{i}</div>
              <div className="col-span-12 md:col-span-3">
                <p className="font-serif text-xl md:text-2xl tracking-tight">{p.phase}</p>
                <p className="text-xs text-[#666666] mt-1">{p.year}</p>
              </div>
              <div className="col-span-12 md:col-span-2 font-serif text-2xl md:text-3xl">{p.capital}</div>
              <div className="col-span-12 md:col-span-6">
                <p className="text-sm leading-relaxed">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Revenue channels */}
      <section className="bg-[#1A1A1A] text-[#F9F9F6]" data-testid="model-revenue">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24">
          <div className="grid grid-cols-12 gap-8 mb-10">
            <div className="col-span-12 md:col-span-6">
              <p className="eyebrow text-[#A8A8A0]">02. Revenue Channels</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-4">Six channels of cash flow.</h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
              <p className="text-sm text-[#C7C7BF] leading-relaxed">Indicative Year 3 and Year 5 revenue per channel. The model isn't single-product. It's a platform.</p>
            </div>
          </div>
          <div className="border-t border-[#333330]">
            <div className="grid grid-cols-12 gap-4 py-3 text-[10px] uppercase tracking-[0.22em] text-[#A8A8A0] border-b border-[#333330]">
              <div className="col-span-6">Channel</div>
              <div className="col-span-3 text-right">Year 3</div>
              <div className="col-span-3 text-right">Year 5</div>
            </div>
            {REVENUE_CHANNELS.map((r) => (
              <div key={r.name} className="grid grid-cols-12 gap-4 py-5 border-b border-[#333330] items-center">
                <div className="col-span-6 font-serif text-lg md:text-xl tracking-tight">{r.name}</div>
                <div className="col-span-3 text-right font-serif text-xl text-[#E5C4A1]">{r.year_3}</div>
                <div className="col-span-3 text-right font-serif text-xl text-[#F9F9F6]">{r.year_5}</div>
              </div>
            ))}
            <div className="grid grid-cols-12 gap-4 py-5 items-center">
              <div className="col-span-6 eyebrow text-[#E5C4A1]">Total revenue</div>
              <div className="col-span-3 text-right font-serif text-2xl text-[#E5C4A1]">{totalY3}</div>
              <div className="col-span-3 text-right font-serif text-2xl text-[#F9F9F6]">{totalY5}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Return logic */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24" data-testid="model-return-logic">
        <div className="grid grid-cols-12 gap-8 mb-10">
          <div className="col-span-12 md:col-span-5">
            <p className="eyebrow">03. Investor Return Logic</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-4">Returns, structured.</h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 self-end">
            <p className="text-sm text-[#1A1A1A] leading-relaxed max-w-xl">
              Three return engines and a self-liquidating debt layer. Capital is repaid before equity participates in upside.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#1A1A1A]">
          {RETURN_LOGIC.map((r) => (
            <div key={r.code} className="border-r border-b border-[#1A1A1A] p-6 md:p-8 hover:bg-white transition-colors">
              <span className="font-serif text-xl text-[#5C2018]">{r.code}</span>
              <h3 className="font-serif text-xl md:text-2xl tracking-tight mt-6 leading-tight">{r.title}</h3>
              <p className="text-sm text-[#1A1A1A] mt-3 leading-relaxed">{r.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Interactive coming soon */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pb-20" data-testid="model-interactive-teaser">
        <div className="border border-[#5C2018]/40 p-8 md:p-12 grid grid-cols-12 gap-6 items-center bg-white">
          <div className="col-span-12 md:col-span-1">
            <Sparkles className="text-[#5C2018]" />
          </div>
          <div className="col-span-12 md:col-span-7">
            <p className="eyebrow text-[#5C2018]">Coming next phase</p>
            <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-3 leading-tight">
              Interactive scenario model. Adjust capital, timeline and offtake mix to see returns recalculate live.
            </h3>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link to="/register" data-testid="model-register-cta" className="inline-flex items-center gap-3 px-6 py-3 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A]">
              Get early access <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <TriCTA />
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <p className="eyebrow">{label}</p>
      <p className="font-serif text-3xl md:text-4xl tracking-tight mt-2">{value}</p>
      <p className="text-xs text-[#666666] mt-1">{sub}</p>
    </div>
  );
}
